import React from 'react';
import { useIntl } from 'react-intl';
import {
  ApproveIcon,
  ProposeAllIcon as BulkForwardIcon
} from '~/modules/common/components/Icons';
import { USER_ACCESS_ROLE } from '~/modules/common/enums';
import { useHasPermission } from '~/modules/common/permissions';
import { useMeContext } from '~/modules/me/useMeContext';
import { ResourceRequestStatus } from '~/types';
import { supporedProjectPolicyRoles } from './useResourceRequestAllocationActions';

export const useResourceRequestAllocationBulkActions = ({
  project,
  resourceRequests,
  onAllResourceRequestsSubmit,
  onAllResourceRequestsUnsubmit,
  onAllResourceRequestsAccept,
  onAllResourceRequestsAllocationReject,
  onAllResourceRequestsFulfilled,
  onAllResourceRequestsReject,
  onAllResourceRequestsRecalled,
  onAllResourceRequestsToBeHired,
  onAllToBeHiredResourceRequestsToSubmit
}) => {
  const { userAccessRoles } = useMeContext();
  const intl = useIntl();

  const { permittedActionUris = [] } = project || {};

  const canEditProject = permittedActionUris.includes(
    'urn:replicon:project-action:edit-project'
  );

  const canEditResourceAssignments = useHasPermission({
    actionUri:
      'urn:replicon:resource-assignment-action:edit-resource-assignments'
  });

  const isUserHasProjectPolicyRole = supporedProjectPolicyRoles.some(sr =>
    userAccessRoles.includes(sr)
  );

  const isUserHasResourceManagerRole = userAccessRoles.includes(
    USER_ACCESS_ROLE.RESOURCE_MANAGER
  );

  const isAnyDraftRequestExists = resourceRequests.some(
    r => r.requestStatus === ResourceRequestStatus.Draft
  );

  const isAnySubmittedRequestExists = resourceRequests.some(
    r =>
      r.requestStatus === ResourceRequestStatus.Submitted &&
      r.resourceAllocations.length > 0
  );

  const isAnyRejectedRequestExists = resourceRequests.some(
    r => r.requestStatus === ResourceRequestStatus.Rejected
  );

  const isAnyToBeHiredRequestsExists = resourceRequests.some(
    r => r.requestStatus === ResourceRequestStatus.Tobehired
  );

  const isAnyTentativeRequestExists = resourceRequests.some(
    r => r.requestStatus === ResourceRequestStatus.Tentative
  );

  const isRejectActionEnabled = resourceRequests.some(
    r =>
      r.requestStatus === ResourceRequestStatus.Submitted &&
      r.resourceAllocations.length === 0
  );

  const isToBeHiredEnabled = resourceRequests.some(
    r =>
      (r.requestStatus === ResourceRequestStatus.Draft ||
        r.requestStatus === ResourceRequestStatus.Submitted ||
        r.requestStatus === ResourceRequestStatus.Rejected) &&
      r.resourceAllocations.length === 0
  );

  const isUnSubmitAllEnabled = resourceRequests.some(
    r =>
      (r.requestStatus === ResourceRequestStatus.Submitted ||
        r.requestStatus === ResourceRequestStatus.Tobehired ||
        r.requestStatus === ResourceRequestStatus.Rejected) &&
      r.resourceAllocations.length === 0
  );

  const isCompleteAllPendingAllocationEnabled = resourceRequests.some(
    r =>
      (r.requestStatus === ResourceRequestStatus.Submitted &&
        r.resourceAllocations.length !== 0) ||
      r.requestStatus === ResourceRequestStatus.Tentative
  );

  const submitAllAction = {
    icon: <BulkForwardIcon />,
    className: 'submitIcon',
    label: intl.formatMessage({
      id: 'resourceRequestActions.submitAllRequests'
    }),
    onClick: onAllResourceRequestsSubmit,
    primary: true
  };

  const unsubmitAllAction = {
    label: intl.formatMessage({
      id: 'resourceRequestActions.unsubmitAllRequests'
    }),
    onClick: onAllResourceRequestsUnsubmit
  };

  const cancelAllToBeHired = {
    label: intl.formatMessage({
      id: 'resourceRequestActions.cancelAllToBeHired'
    }),
    onClick: onAllToBeHiredResourceRequestsToSubmit
  };

  const isAcceptAllActionPrimaryAction = !(
    isAnyDraftRequestExists ||
    isAnyRejectedRequestExists ||
    isCompleteAllPendingAllocationEnabled
  );

  const acceptAllAction = {
    icon: isAcceptAllActionPrimaryAction ? <ApproveIcon /> : null,
    className: isAcceptAllActionPrimaryAction ? 'approveIcon' : '',
    label: intl.formatMessage({
      id: 'resourceRequestActions.acceptAllResources'
    }),
    onClick: onAllResourceRequestsAccept,
    primary: isAcceptAllActionPrimaryAction
  };

  const rejectAllAction = {
    label: intl.formatMessage({
      id: 'resourceRequestActions.rejectAllResources'
    }),
    onClick: onAllResourceRequestsAllocationReject,
    primary: false
  };

  const isProposeAllResourcesPrimaryAction = !(
    isAnyDraftRequestExists ||
    isAnyRejectedRequestExists ||
    isAnyTentativeRequestExists
  );

  const proposeAllResources = {
    icon: isProposeAllResourcesPrimaryAction ? <BulkForwardIcon /> : null,
    className: isProposeAllResourcesPrimaryAction ? 'submitIcon' : '',
    label: intl.formatMessage({
      id: 'resourceAllocationActions.proposeAllResources'
    }),
    onClick: onAllResourceRequestsFulfilled,
    primary: isProposeAllResourcesPrimaryAction
  };

  const rejectAllRequests = {
    label: intl.formatMessage({
      id: 'resourceAllocationActions.rejectAllRequests'
    }),
    onClick: onAllResourceRequestsReject
  };

  const recallAllActionRequests = {
    label: intl.formatMessage({
      id: 'resourceAllocationActions.recallAllResources'
    }),
    onClick: onAllResourceRequestsRecalled
  };

  const markAllToBeHired = {
    label: intl.formatMessage({
      id: 'resourceAllocationActions.markAllToBeHired'
    }),
    onClick: onAllResourceRequestsToBeHired
  };

  const showSubmitAction =
    canEditProject && (isAnyDraftRequestExists || isAnyRejectedRequestExists);

  const showProposeAction =
    canEditResourceAssignments &&
    isAnySubmittedRequestExists &&
    isUserHasResourceManagerRole;

  const showRejectAllRequestsAction =
    canEditResourceAssignments &&
    isRejectActionEnabled &&
    isUserHasResourceManagerRole;

  const showToBeHiredAction =
    canEditResourceAssignments &&
    isToBeHiredEnabled &&
    isUserHasResourceManagerRole;

  const showCancelToBeHiredAction =
    (canEditProject || canEditResourceAssignments) &&
    isAnyToBeHiredRequestsExists &&
    isUserHasResourceManagerRole;

  const showRecallAllRequestsAction =
    (canEditProject || canEditResourceAssignments) &&
    isAnyTentativeRequestExists &&
    isUserHasResourceManagerRole;

  return (isUserHasProjectPolicyRole
    ? [
        ...(showSubmitAction ? [submitAllAction] : []),
        ...(canEditProject && isAnyTentativeRequestExists
          ? [acceptAllAction]
          : []),
        ...(showProposeAction ? [proposeAllResources] : []),
        ...(canEditProject && isAnyTentativeRequestExists
          ? [rejectAllAction]
          : []),
        ...(canEditProject && isUnSubmitAllEnabled ? [unsubmitAllAction] : []),
        ...(showRejectAllRequestsAction ? [rejectAllRequests] : []),
        ...(showToBeHiredAction ? [markAllToBeHired] : []),
        ...(showCancelToBeHiredAction ? [cancelAllToBeHired] : []),
        ...(showRecallAllRequestsAction ? [recallAllActionRequests] : [])
      ]
    : [
        ...(showProposeAction ? [proposeAllResources] : []),
        ...(showRejectAllRequestsAction ? [rejectAllRequests] : []),
        ...(showToBeHiredAction ? [markAllToBeHired] : []),
        ...(showCancelToBeHiredAction ? [cancelAllToBeHired] : []),
        ...(showRecallAllRequestsAction ? [recallAllActionRequests] : [])
      ]
  ).filter(r => r);
};

export default useResourceRequestAllocationBulkActions;
